import * as v from "valibot";

import Bugsnag from "@/utils/bugsnag";
import {
  type DisplayEvent,
  toDisplayEvent,
  upcomingRowToDisplayEvent,
} from "@/utils/event-helpers";
import {
  type EventMemberDetail,
  EventMemberDetailSchema,
  UpcomingEventRowSchema,
} from "@/utils/schemas";
import { supabase } from "@/utils/supabase";

const EVENT_SELECT = "*, event_ticket_kind(*)";

export interface EventGuest {
  isMutual: boolean;
  isSelf: boolean;
  name: string;
  role: string;
  user_id: string;
}

export interface EventMembers {
  guests: EventGuest[];
  hosts: string[];
  myStatus: null | string;
}

export async function deleteTicketKind(ticketKindId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("event_ticket_kind")
      .update({ price_cents: 0, quantity_total: null })
      .eq("id", ticketKindId);
    if (error) {
      console.error("deleteTicketKind error", error);
      Bugsnag.notify(new Error(`deleteTicketKind error: ${error.message}`));
      return false;
    }
    return true;
  } catch (e) {
    console.error("deleteTicketKind error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return false;
  }
}

export async function fetchAllEvents(): Promise<DisplayEvent[]> {
  try {
    const { data, error } = await supabase
      .from("v1_upcoming_events")
      .select("*");

    if (error) throw error;
    if (!data) return [];
    return v
      .parse(v.array(UpcomingEventRowSchema), data)
      .map(upcomingRowToDisplayEvent);
  } catch (e) {
    console.error("fetchAllEvents error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return [];
  }
}

export async function fetchEventBySlug(
  slug: string,
): Promise<DisplayEvent | null> {
  try {
    const { data, error } = await supabase
      .from("event")
      .select(EVENT_SELECT)
      .eq("slug", slug)
      .single();

    if (error) throw error;
    if (!data) return null;
    return toDisplayEvent(data);
  } catch (e) {
    console.error("fetchEventBySlug error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return null;
  }
}

export async function fetchEventMemberDetails(
  eventId: string,
): Promise<EventMemberDetail[]> {
  try {
    const { data: members, error } = await supabase
      .from("v1_event_member_detail")
      .select("user_id, role, rsvp_status, name, is_contact")
      .eq("event_id", eventId);

    Bugsnag.leaveBreadcrumb("fetchEventMemberDetails", {
      error: String(error),
      memberCount: String(members?.length ?? 0),
    });
    if (error) {
      throw error;
    }

    return v.parse(v.array(EventMemberDetailSchema), members);
  } catch (e) {
    console.error("fetchEventMemberDetails error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return [];
  }
}

export async function fetchEventRaw(id: string) {
  try {
    const { data, error } = await supabase
      .from("event")
      .select(EVENT_SELECT)
      .eq("id", id)
      .single();

    if (error) throw error;
    if (!data) return null;
    return data;
  } catch (e) {
    console.error("fetchEventRaw error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return null;
  }
}

export async function fetchEventRawBySlug(slug: string) {
  try {
    const { data, error } = await supabase
      .from("event")
      .select(EVENT_SELECT)
      .eq("slug", slug)
      .single();

    if (error) throw error;
    if (!data) return null;
    return data;
  } catch (e) {
    console.error("fetchEventRawBySlug error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return null;
  }
}

export async function fetchSingleEventMember(
  eventId: string,
  userId: string,
): Promise<EventMemberDetail | null> {
  try {
    const { data, error } = await supabase
      .from("v1_event_member_detail")
      .select("user_id, role, rsvp_status, name, is_contact")
      .eq("event_id", eventId)
      .eq("user_id", userId)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return v.parse(EventMemberDetailSchema, data);
  } catch (e) {
    console.error("fetchSingleEventMember error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return null;
  }
}

export async function insertEvent(
  eventData: Record<string, unknown>,
): Promise<{ id: string } | null> {
  try {
    const { data, error } = await supabase
      .from("event")
      .insert(eventData)
      .select("id")
      .single();

    if (error || !data) {
      console.error("insertEvent error", error);
      Bugsnag.notify(new Error(`insertEvent error: ${error?.message}`));
      return null;
    }
    return data;
  } catch (e) {
    console.error("insertEvent error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return null;
  }
}

export async function updateEvent(
  id: string,
  eventData: Record<string, unknown>,
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("event")
      .update(eventData)
      .eq("id", id);

    if (error) {
      console.error("updateEvent error", error);
      Bugsnag.notify(new Error(`updateEvent error: ${error.message}`));
      return false;
    }
    return true;
  } catch (e) {
    console.error("updateEvent error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return false;
  }
}

export async function upsertRsvpStatus(
  eventId: string,
  userId: string,
  rsvpStatus: "cancelled" | "confirmed" | "maybe",
): Promise<boolean> {
  try {
    const { error } = await supabase.from("event_member").upsert(
      {
        event_id: eventId,
        rsvp_status: rsvpStatus,
        user_id: userId,
      },
      { onConflict: "event_id,user_id" },
    );
    if (error) {
      console.error("upsertRsvpStatus error", error);
      Bugsnag.notify(new Error(`upsertRsvpStatus error: ${error.message}`));
      return false;
    }
    return true;
  } catch (e) {
    console.error("upsertRsvpStatus error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return false;
  }
}

export async function upsertTicketKind(
  eventId: string,
  ticketData: {
    currency: string;
    description?: string;
    name?: string;
    price_cents: number;
    quantity_total?: number;
  },
  existingTicketKindId?: null | string,
): Promise<boolean> {
  try {
    if (existingTicketKindId) {
      const { error } = await supabase
        .from("event_ticket_kind")
        .update(ticketData)
        .eq("id", existingTicketKindId);
      if (error) {
        console.error("upsertTicketKind update error", error);
        Bugsnag.notify(
          new Error(`upsertTicketKind update error: ${error.message}`),
        );
        return false;
      }
    } else {
      const { error } = await supabase
        .from("event_ticket_kind")
        .insert({ event_id: eventId, ...ticketData });
      if (error) {
        console.error("upsertTicketKind insert error", error);
        Bugsnag.notify(
          new Error(`upsertTicketKind insert error: ${error.message}`),
        );
        return false;
      }
    }
    return true;
  } catch (e) {
    console.error("upsertTicketKind error", e);
    Bugsnag.notify(e instanceof Error ? e : new Error(String(e)));
    return false;
  }
}
